// AUTH-001 — Unified approval inbox for the generic engine.
//
// One page for EVERY engine-wired action (cost rates, standards, stock
// adjustments, salary structures, activations, role grants, corrections,
// voucher reversals). Tabs: Pending For Me / My Requests / History /
// Escalated / Cancelled. Acting goes through the single generic
// POST /approvals/{id}/act endpoint — no per-module action endpoints.
// Deep-link: /approvals/my-pending?request_id=<id> opens the detail.
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Badge, Button, Card, Descriptions, Drawer, Input, Modal, Popconfirm,
  Progress, Space, Table, Tabs, Tag, Tooltip, Typography, message,
} from 'antd'
import {
  AuditOutlined, CheckOutlined, CloseOutlined, EyeOutlined, RollbackOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'

const { Text } = Typography

interface ApprovalRow {
  id: string
  entity_name: string
  entity_id: string | null
  action: string
  status: string
  current_tier: number
  total_tiers: number
  requested_by: string
  requested_by_name: string | null
  requested_at: string
  assignee_role: string | null
  assignee_user: string | null
  amount_value: number | null
  risk_level: string | null
  reason: string | null
  payload: Record<string, unknown> | null
  due_at: string | null
  escalated_at: string | null
  escalated_to_role: string | null
  decided_by_name: string | null
  decided_at: string | null
  rejection_reason: string | null
  sole_admin_self_approve?: boolean
  timeline?: {
    tier: number; decision: string; acted_by_name: string | null
    acted_at: string; remarks: string | null
  }[]
}

const STATUS_COLOR: Record<string, string> = {
  PENDING: 'processing', APPROVED: 'success', REJECTED: 'error',
  CANCELLED: 'default', ESCALATED: 'warning', EXPIRED: 'default',
}
const RISK_COLOR: Record<string, string> = {
  LOW: 'blue', MEDIUM: 'orange', HIGH: 'red',
}

// Friendly labels for the engine action codes
const ACTION_LABEL: Record<string, string> = {
  'cost.rate.update': 'Cost Rate Change',
  'production.standard.update': 'Production Standard Change',
  'inventory.stock.adjust': 'Stock Adjustment',
  'hr.salary_structure.update': 'Salary Structure Change',
  'hr.employee.activate': 'Employee Activation',
  'admin.user_access.grant': 'User Role Grant',
  'production.reopen': 'Production Log Reopen',
  'grn.correct': 'GRN Correction',
  'recipe.revoke': 'Recipe Revoke',
  'payroll.recompute': 'Payroll Period Reopen',
  'sales.cancel': 'Sales Authorisation Cancel',
  'po.cancel': 'PO Cancel',
  'voucher.reverse': 'Voucher Reversal',
}

// Entity types that have a full-detail "review" screen the approver can open
// to verify the whole application + documents before deciding. Employee
// activations open the joining detail (all tabs, docs, photo).
const REVIEW_ROUTE: Record<string, (id: string) => string> = {
  employee: (id) => `/hr/joining/${id}`,
  spare_request: (id) => `/stores/spare-requests/${id}`,
  spare_indent: () => `/stores/purchase-indents`,
  spare_cs: () => `/stores/comparative-statements`,
}

function reviewPath(r: { entity_name: string; entity_id: string | null }): string | null {
  if (!r.entity_id) return null
  const fn = REVIEW_ROUTE[r.entity_name]
  return fn ? fn(r.entity_id) : null
}

function openReview(r: { entity_name: string; entity_id: string | null }) {
  const path = reviewPath(r)
  if (path) window.open(path, '_blank', 'noopener,noreferrer')
}

function PayloadDiff({ payload }: { payload: Record<string, unknown> | null }) {
  if (!payload) return <Text type="secondary">—</Text>
  // Known shape: {changes: {field: {old, new}}} (cost rates, GRN correct)
  const changes = payload.changes as
    | Record<string, { old?: unknown; new?: unknown }> | undefined
  if (changes && typeof changes === 'object') {
    return (
      <div>
        {Object.entries(changes).map(([field, c]) => (
          <div key={field}>
            <Text code>{field}</Text>{' '}
            <Text delete type="secondary">{String(c.old ?? '—')}</Text>
            {' → '}
            <Text strong>{String(c.new ?? '—')}</Text>
          </div>
        ))}
      </div>
    )
  }
  // Generic: render scalar entries
  const entries = Object.entries(payload)
    .filter(([, v]) => typeof v !== 'object' || v === null)
  return (
    <div>
      {entries.map(([k, v]) => (
        <div key={k}>
          <Text type="secondary">{k}:</Text> <Text>{String(v ?? '—')}</Text>
        </div>
      ))}
    </div>
  )
}

export default function MyPendingApprovalsPage() {
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const [tab, setTab] = useState('pending')
  const [detailId, setDetailId] = useState<string | null>(null)
  const [rejectFor, setRejectFor] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  const pendingQ = useQuery<ApprovalRow[]>({
    queryKey: ['approvals-my-pending'],
    queryFn: () => apiClient.get('/approvals/my-pending')
      .then((r: { data: ApprovalRow[] }) => r.data),
    refetchInterval: 30_000,
  })
  const mineQ = useQuery<ApprovalRow[]>({
    queryKey: ['approvals-my-requests'],
    queryFn: () => apiClient.get('/approvals/my-requests')
      .then((r: { data: ApprovalRow[] }) => r.data),
  })
  const historyQ = useQuery<ApprovalRow[]>({
    queryKey: ['approvals-history'],
    queryFn: () => apiClient.get('/approvals/history')
      .then((r: { data: ApprovalRow[] }) => r.data),
  })
  const detailQ = useQuery<ApprovalRow>({
    queryKey: ['approval-detail', detailId],
    queryFn: () => apiClient.get(`/approvals/${detailId}`)
      .then((r: { data: ApprovalRow }) => r.data),
    enabled: !!detailId,
  })

  // Deep-link from notifications: ?request_id=<uuid>
  useEffect(() => {
    const rid = params.get('request_id')
    if (rid) setDetailId(rid)
  }, [params])

  const actMut = useMutation({
    mutationFn: ({ id, decision, remarks }:
      { id: string; decision: string; remarks?: string }) =>
      apiClient.post(`/approvals/${id}/act`, { decision, remarks })
        .then((r: { data: unknown }) => r.data),
    onSuccess: (r: any, vars) => {
      qc.invalidateQueries({ queryKey: ['approvals-my-pending'] })
      qc.invalidateQueries({ queryKey: ['approvals-my-requests'] })
      qc.invalidateQueries({ queryKey: ['approvals-history'] })
      qc.invalidateQueries({ queryKey: ['approval-detail'] })
      if (vars.decision === 'APPROVE') {
        message.success(r?.applied
          ? 'Approved and applied'
          : `Approved — advanced to tier ${r?.current_tier} of ${r?.total_tiers}`)
      } else if (vars.decision === 'REJECT') {
        message.info('Rejected')
      } else {
        message.info('Request recalled')
      }
      setRejectFor(null); setRejectReason('')
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.detail?.message
        ?? e?.response?.data?.detail ?? 'Action failed'),
  })

  const [soleApprove, setSoleApprove] = useState<{ id: string } | null>(null)
  const [soleReason, setSoleReason] = useState('')

  const confirmSoleApprove = () => {
    if (!soleApprove || !soleReason.trim()) return
    actMut.mutate(
      { id: soleApprove.id, decision: 'APPROVE', remarks: soleReason.trim() },
      { onSettled: () => { setSoleApprove(null); setSoleReason('') } },
    )
  }

  const myRequests = mineQ.data ?? []
  const escalated = useMemo(
    () => [...(pendingQ.data ?? []), ...myRequests]
      .filter(r => r.escalated_at != null),
    [pendingQ.data, myRequests])
  const cancelled = useMemo(
    () => myRequests.filter(r => r.status === 'CANCELLED'),
    [myRequests])

  const baseColumns: any[] = [
    {
      title: 'Action', dataIndex: 'action', key: 'action', width: 200,
      render: (v: string, r: ApprovalRow) => (
        <a onClick={() => setDetailId(r.id)}>{ACTION_LABEL[v] ?? v}</a>
      ),
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 110,
      render: (v: string, r: ApprovalRow) => (
        <Space size={4}>
          <Tag color={STATUS_COLOR[v]}>{v}</Tag>
          {r.escalated_at && <Tag color="warning">ESC</Tag>}
        </Space>
      ),
    },
    {
      title: 'Tier', key: 'tier', width: 110,
      render: (_: unknown, r: ApprovalRow) => (
        <Tooltip title={`Tier ${r.current_tier} of ${r.total_tiers}`}>
          <Progress
            percent={Math.round(100 * (r.current_tier - (r.status === 'PENDING' ? 1 : 0)) / r.total_tiers)}
            steps={r.total_tiers} size="small" showInfo={false} />
        </Tooltip>
      ),
    },
    {
      title: 'Requested By', dataIndex: 'requested_by_name',
      key: 'requested_by_name', width: 140,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: 'When', dataIndex: 'requested_at', key: 'requested_at',
      width: 130,
      render: (v: string) => dayjs(v).format('DD MMM HH:mm'),
    },
    {
      title: 'Risk', dataIndex: 'risk_level', key: 'risk_level', width: 90,
      render: (v: string | null) =>
        v ? <Tag color={RISK_COLOR[v]}>{v}</Tag> : '—',
    },
    {
      title: 'Value', dataIndex: 'amount_value', key: 'amount_value',
      width: 110,
      render: (v: number | null) =>
        v != null ? `₹${v.toLocaleString('en-IN')}` : '—',
    },
    { title: 'Reason', dataIndex: 'reason', key: 'reason', ellipsis: true },
  ]

  const pendingColumns: any[] = [
    ...baseColumns,
    {
      title: 'Due', dataIndex: 'due_at', key: 'due_at', width: 130,
      render: (v: string | null) => v ? (
        <Text type={dayjs(v).isBefore(dayjs()) ? 'danger' : undefined}>
          {dayjs(v).format('DD MMM HH:mm')}
        </Text>
      ) : '—',
    },
    {
      title: 'Actions', key: 'act', width: 250, fixed: 'right' as const,
      render: (_: unknown, r: ApprovalRow) => (
        <Space>
          {reviewPath(r) && (
            <Tooltip title="Open the full application + documents to verify">
              <Button size="small" icon={<EyeOutlined />}
                onClick={() => openReview(r)}>Review</Button>
            </Tooltip>
          )}
          {r.sole_admin_self_approve ? (
            <Button size="small" type="primary" icon={<CheckOutlined />}
              onClick={() => { setSoleApprove({ id: r.id }); setSoleReason('') }}>
              Approve
            </Button>
          ) : (
            <Popconfirm
              title="Approve this request?"
              description={r.current_tier >= r.total_tiers
                ? 'Final tier — the change will be applied immediately.'
                : 'Will advance to the next approval tier.'}
              onConfirm={() => actMut.mutate({ id: r.id, decision: 'APPROVE' })}
            >
              <Button size="small" type="primary" icon={<CheckOutlined />}
                loading={actMut.isPending}>Approve</Button>
            </Popconfirm>
          )}
          <Button size="small" danger icon={<CloseOutlined />}
            onClick={() => setRejectFor(r.id)}>Reject</Button>
        </Space>
      ),
    },
  ]

  const myReqColumns: any[] = [
    ...baseColumns,
    {
      title: 'Actions', key: 'act', width: 110, fixed: 'right' as const,
      render: (_: unknown, r: ApprovalRow) => r.status === 'PENDING' ? (
        <Popconfirm title="Recall this request?"
          onConfirm={() => actMut.mutate({ id: r.id, decision: 'RECALL' })}>
          <Button size="small" icon={<RollbackOutlined />}>Recall</Button>
        </Popconfirm>
      ) : null,
    },
  ]

  const mkTable = (rows: ApprovalRow[], cols: any[],
                   loading: boolean) => (
    <Table
      rowKey="id" size="small" dataSource={rows} columns={cols as never}
      loading={loading} pagination={{ pageSize: 15, showSizeChanger: true }}
      scroll={{ x: 1200 }}
      locale={{ emptyText: loading ? ' ' : 'Nothing here' }}
    />
  )

  const detail = detailQ.data

  return (
    <div style={{ padding: 16 }}>
      <Space align="center" style={{ marginBottom: 12 }}>
        <AuditOutlined style={{ fontSize: 22 }} />
        <Typography.Title level={4} style={{ margin: 0 }}>
          My Approvals
        </Typography.Title>
      </Space>

      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'pending',
            label: <Badge count={pendingQ.data?.length ?? 0} size="small"
              offset={[10, 0]}>Pending For Me</Badge>,
            children: mkTable(pendingQ.data ?? [], pendingColumns,
              pendingQ.isLoading),
          },
          {
            key: 'mine', label: 'My Requests',
            children: mkTable(myRequests.filter(r => r.status === 'PENDING'),
              myReqColumns, mineQ.isLoading),
          },
          {
            key: 'history', label: 'Approved / Rejected',
            children: mkTable(historyQ.data ?? [], baseColumns,
              historyQ.isLoading),
          },
          {
            key: 'escalated',
            label: <Badge count={escalated.length} size="small"
              offset={[10, 0]}>Escalated</Badge>,
            children: mkTable(escalated, pendingColumns, false),
          },
          {
            key: 'cancelled', label: 'Recalled / Cancelled',
            children: mkTable(cancelled, baseColumns, false),
          },
        ]}
      />

      {/* Detail drawer (also the notification deep-link target) */}
      <Drawer
        open={!!detailId}
        width={620}
        title={detail ? (ACTION_LABEL[detail.action] ?? detail.action)
          : 'Approval Request'}
        onClose={() => {
          setDetailId(null)
          if (params.get('request_id')) {
            params.delete('request_id'); setParams(params, { replace: true })
          }
        }}
      >
        {detailQ.isError && (
          <Alert type="error" message="Could not load this request — you may not be involved in it." />
        )}
        {detail && (
          <>
            {reviewPath(detail) && (
              <Button type="primary" ghost block icon={<EyeOutlined />}
                style={{ marginBottom: 12 }}
                onClick={() => openReview(detail)}>
                Review Full Application &amp; Documents ↗
              </Button>
            )}
            {detail.status === 'PENDING' && (
              <Space style={{ marginBottom: 12, width: '100%' }}>
                {detail.sole_admin_self_approve ? (
                  <Button type="primary" icon={<CheckOutlined />}
                    onClick={() => { setSoleApprove({ id: detail.id }); setSoleReason('') }}>
                    Approve
                  </Button>
                ) : (
                  <Popconfirm
                    title="Approve this request?"
                    description={detail.current_tier >= detail.total_tiers
                      ? 'Final tier — the change will be applied immediately.'
                      : 'Will advance to the next approval tier.'}
                    onConfirm={() => actMut.mutate({ id: detail.id, decision: 'APPROVE' })}
                  >
                    <Button type="primary" icon={<CheckOutlined />}
                      loading={actMut.isPending}>Approve</Button>
                  </Popconfirm>
                )}
                <Button danger icon={<CloseOutlined />}
                  onClick={() => setRejectFor(detail.id)}>Reject</Button>
              </Space>
            )}
            {detail.escalated_at && (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                message={`Escalated to ${detail.escalated_to_role ?? 'higher tier'} on ${dayjs(detail.escalated_at).format('DD MMM HH:mm')}`} />
            )}
            <Descriptions column={2} size="small" bordered
              style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Status" span={1}>
                <Tag color={STATUS_COLOR[detail.status]}>{detail.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Tier">
                {detail.current_tier} / {detail.total_tiers}
              </Descriptions.Item>
              <Descriptions.Item label="Module">
                {detail.entity_name}
              </Descriptions.Item>
              <Descriptions.Item label="Document ID">
                <Text copyable={{ text: detail.entity_id ?? '' }}>
                  {detail.entity_id?.slice(0, 8) ?? '—'}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Requested By">
                {detail.requested_by_name ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Requested At">
                {dayjs(detail.requested_at).format('DD MMM YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Assigned To">
                {detail.assignee_role ?? detail.assignee_user ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Due">
                {detail.due_at
                  ? dayjs(detail.due_at).format('DD MMM HH:mm') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Risk">
                {detail.risk_level
                  ? <Tag color={RISK_COLOR[detail.risk_level]}>{detail.risk_level}</Tag>
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Value">
                {detail.amount_value != null
                  ? `₹${detail.amount_value.toLocaleString('en-IN')}` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Reason" span={2}>
                {detail.reason ?? '—'}
              </Descriptions.Item>
              {detail.rejection_reason && (
                <Descriptions.Item label="Rejection" span={2}>
                  <Text type="danger">{detail.rejection_reason}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>

            <Card size="small" title="Proposed Change"
              style={{ marginBottom: 16 }}>
              <PayloadDiff payload={detail.payload} />
            </Card>

            <Card size="small" title="Approval Timeline">
              {(detail.timeline ?? []).length === 0 && (
                <Text type="secondary">No decisions yet</Text>
              )}
              {(detail.timeline ?? []).map((t, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                  <Tag color={t.decision === 'APPROVE' ? 'green'
                    : t.decision === 'REJECT' ? 'red' : 'default'}>
                    {t.decision}
                  </Tag>
                  <Text strong>{t.acted_by_name ?? 'Unknown'}</Text>
                  <Text type="secondary">
                    {' '}· tier {t.tier} · {dayjs(t.acted_at).format('DD MMM HH:mm')}
                  </Text>
                  {t.remarks && (
                    <div><Text type="secondary">{t.remarks}</Text></div>
                  )}
                </div>
              ))}
            </Card>
          </>
        )}
      </Drawer>

      {/* Reject modal */}
      <Modal
        open={!!rejectFor}
        title="Reject request"
        okText="Reject" okButtonProps={{ danger: true }}
        onOk={() => {
          if (rejectReason.trim().length < 3) {
            message.warning('Please give a reason'); return
          }
          actMut.mutate({ id: rejectFor!, decision: 'REJECT',
            remarks: rejectReason })
        }}
        onCancel={() => { setRejectFor(null); setRejectReason('') }}
      >
        <Input.TextArea rows={3} value={rejectReason}
          onChange={e => setRejectReason(e.target.value)}
          placeholder="Why is this being rejected?" />
      </Modal>

      {/* Sole-admin self-approve modal — reason required */}
      <Modal
        open={!!soleApprove}
        title="Approve your own request (sole administrator)"
        okText="Approve"
        okButtonProps={{ disabled: !soleReason.trim(), loading: actMut.isPending }}
        onOk={confirmSoleApprove}
        onCancel={() => { setSoleApprove(null); setSoleReason('') }}>
        <p>You are the only approver for this request. Approving your own
          request as sole administrator — a reason is required and will be
          recorded in the audit log.</p>
        <Input.TextArea rows={3} value={soleReason}
          onChange={(e) => setSoleReason(e.target.value)}
          placeholder="Reason (required)" />
      </Modal>
    </div>
  )
}
